"use client"

import styled from "@emotion/styled"
import { Box, Button, Typography } from "@mui/material"
import Image from "next/image"
import { CustomImage } from "ui"
import Container from "ui/Container"
import { sharedColors } from "ui/theme/colors"
import { mediaQueries } from "ui/theme/mediaQueries"
import { extractProperty } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

const OuterContainer = styled.div<{ $isGray?: boolean }>`
  width: 100%;
  background-color: ${({ $isGray }) =>
    $isGray ? sharedColors.mediumGrayLight : "transparent"};
`

interface AppBannerProps {
  data: any
  blockIndex?: number
  isHomepage?: boolean
}

// Layout containers refined for provided design
const StyledRoot = styled(Box)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column; /* phone under text on mobile */
  overflow: hidden;
  border-radius: 12px;
  background-color: ${sharedColors.white};
  /* box-shadow: 0 15px 25px 0 rgba(38, 48, 61, 0.1); */
  gap: 32px;
  ${mediaQueries.desktopM} {
    flex-direction: row;
    align-items: flex-start;
    min-height: 420px;
    gap: 56px;
  }
`

const StyledContent = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 2;
  text-align: start;
  align-self: center;
  padding: 0 24px 24px 24px;
  ${mediaQueries.desktopM} {
    flex: 1;
    max-width: 520px;
    padding: 0;
  }
  ul {
    margin: 0;
    padding: 0 0 0 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  ul li {
    position: relative;
    padding-inline-start: 18px;
  }
  ul li::before {
    content: "";
    position: absolute;
    top: 10px;
    inset-inline-start: 6px;
    width: 4px;
    height: 4px;
    background: ${sharedColors.black};
    border-radius: 50%;
  }
`

const StyledButtonsRow = styled(Box)`
  display: flex;
  gap: 16px;
  align-items: center;
  width: 100%;
  flex-direction: column;
  ${mediaQueries.desktopM} {
    flex-direction: row;
    margin-top: 8px;
  }
`

const StoreButton = styled(Button)`
  padding: 0 !important;
  min-width: auto;
  border-radius: 12px;
  background: transparent;
  box-shadow: none !important;
  &:hover {
    background: transparent;
  }
  width: 170px;
  height: 57px;
  ${mediaQueries.desktopM} {
    width: 200px;
    height: 67px;
  }
`
const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 422px;
  img {
    object-fit: contain;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 570px;
    width: 528px;
    img {
      object-fit: contain;
    }
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    height: 534px;
    width: 721px;
  }
`

const TopHeader = styled.div`
  display: flex;
  align-items: end;
  gap: 12px;
`

const StyledSubtitle = styled(Typography)`
  /* max-width: 300px; */
`

export default function AppBanner({ data, blockIndex, isHomepage }: AppBannerProps) {
  const isGray = isHomepage && typeof blockIndex === "number" && blockIndex % 2 === 1
  // Extract Umbraco data
  const imageForDesktop = extractProperty(data, "imageForDesktop", true)
  const imageUrl = imageForDesktop?.[0]?.url
  const altText = imageForDesktop?.[0]?.altText

  const imageForMobile = extractProperty(data, "imageForMobile")
  const title = extractProperty(data, "title")
  const eegLogo = extractProperty(data, "eegLogo")
  const subtitle = extractProperty(data, "subtitle")
  const content = extractProperty(data, "content") // bullet list rich text or line breaks
  const subTitle2 = extractProperty(data, "subTitle2")
  const googleButtonImage = extractProperty(data, "googleButtonImage")
  const googleLink = extractProperty(data, "googleLink")?.[0]?.url
  const appleButtonImage = extractProperty(data, "appleButtonImage")
  const appleLink = extractProperty(data, "appleLink")?.[0]?.url
  // Prepare bullets: if content comes as rich text list (<ul>...</ul>) keep it; else split by newline
  let listHtml = ""
  if (content) {
    if (/(<li[\s>]|<ul[\s>])/i.test(content)) {
      // Already contains list markup
      listHtml = content.replace(/<ul[^>]*>/gi, "").replace(/<\/ul>/gi, "") // strip wrapping UL if present to re-wrap with our styles
    } else {
      const items = content
        .split(/\r?\n|<br\s*\/>/)
        .map((s: string) => s.trim())
        .filter(Boolean)
      listHtml = items.map((i: string) => `<li>${i}</li>`).join("")
    }
  }

  return (
    <OuterContainer $isGray={isGray}>
      <Container>
        <StyledRoot>
        <ImageContainer>
          <CustomImage
            src={getUmbracoMediaUrl(imageUrl) || ""}
            alt={altText || ""}
            fill
          />
        </ImageContainer>
        <StyledContent>
          <TopHeader>
            {title && (
              <Typography variant="h1" component="h2" color="#263677">
                {title}
              </Typography>
            )}
            {eegLogo && (
              <Box sx={{ position: "relative", width: 90, height: 37 }}>
                <Image alt="" src={getUmbracoMediaUrl(eegLogo) || ""} fill />
              </Box>
            )}
          </TopHeader>
          {subtitle && (
            <StyledSubtitle variant="h3" component="p" color="#263677">
              {subtitle}
            </StyledSubtitle>
          )}
          {listHtml && (
            <Typography
              variant="body1"
              color="#263677"
              fontWeight={600}
              dangerouslySetInnerHTML={{ __html: listHtml }}
            />
          )}
          {subTitle2 && (
            <StyledSubtitle variant="h3" component="p" color="#263677">
              {subTitle2}
            </StyledSubtitle>
          )}
          <StyledButtonsRow>
            {appleButtonImage && appleLink && (
              <StoreButton
                variant="text"
                href={appleLink}
                target="_blank"
                aria-label="App Store"
              >
                <Image
                  alt="App Store"
                  src={getUmbracoMediaUrl(appleButtonImage) || ""}
                  fill
                />
              </StoreButton>
            )}
            {googleButtonImage && googleLink && (
              <StoreButton
                variant="text"
                href={googleLink}
                rel="noopener noreferrer"
                aria-label="Google Play"
              >
                <Image
                  alt="Google Play"
                  src={getUmbracoMediaUrl(googleButtonImage) || ""}
                  fill
                />
              </StoreButton>
            )}
          </StyledButtonsRow>
        </StyledContent>
        </StyledRoot>
      </Container>
    </OuterContainer>
  )
}
